import { pageTitle } from '@palaccount/config';
import type { PartnerGroupResponse } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePaymentTerms } from '@/features/admin/payment-terms-api';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { localizeServerMessage } from '@/lib/server-messages';

import { usePartnerGroupMutations, usePartnerGroups } from './groups-api';

interface GroupFormState {
  name: string;
  defaultPaymentTermId: string;
}

const EMPTY: GroupFormState = { name: '', defaultPaymentTermId: '' };

/**
 * Partner Groups (MD-01 field 4, MD-RULE-9) — the list the partner form's
 * Group field picks from: "a grouping used for reporting and for default
 * terms". The columns say what each group DOES: its default term, and how
 * many partners are in it (the MD-RULE-4 delete pre-check — the count links
 * to the filtered partner list, the reporting half).
 */
export function PartnerGroupsPage() {
  const { t } = useTranslation();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<PartnerGroupResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [values, setValues] = useState<GroupFormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('pgroups.title'));
  }, [t]);

  const { data: groups, isPending } = usePartnerGroups(showInactive);
  const { data: terms } = usePaymentTerms();
  const { create, update, setActive, remove } = usePartnerGroupMutations();

  const openNew = () => {
    setEditing(null);
    setValues(EMPTY);
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (group: PartnerGroupResponse) => {
    setEditing(group);
    setValues({
      name: group.name,
      defaultPaymentTermId: group.defaultPaymentTermId ?? '',
    });
    setError(null);
    setFormOpen(true);
  };

  const fail = (cause: unknown) =>
    setError(localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)));

  const submit = async () => {
    setError(null);
    if (values.name.trim() === '') return setError(t('pgroups.errName'));
    try {
      if (editing) {
        await update.mutateAsync({
          groupId: editing.id,
          body: {
            name: values.name.trim(),
            defaultPaymentTermId:
              values.defaultPaymentTermId === '' ? null : values.defaultPaymentTermId,
          },
        });
      } else {
        await create.mutateAsync({
          name: values.name.trim(),
          ...(values.defaultPaymentTermId === ''
            ? {}
            : { defaultPaymentTermId: values.defaultPaymentTermId }),
        });
      }
      setFormOpen(false);
    } catch (cause) {
      fail(cause);
    }
  };

  const removeGroup = async (group: PartnerGroupResponse) => {
    // MD-RULE-3 / §4.20: even an unused record asks before deleting. The
    // asking is GEN-58's sentence in the shared box (GEN-204/GEN-209), raised
    // by the button; this runs once the person has said Delete.
    setDeleting(false);
    try {
      await remove.mutateAsync({ groupId: group.id });
      setFormOpen(false);
    } catch (cause) {
      fail(cause);
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('pgroups.title')}</h2>
        <Gated permission="crm.create" reasonKey="perm.cannot.createPartnerGroups">
          <Button onClick={openNew}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      {formOpen ? (
        <Card>
          <CardHeader>
            <CardTitle>{t(editing ? 'pgroups.editTitle' : 'pgroups.newTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="pg-name">{t('pgroups.fName')}</Label>
                <Input
                  id="pg-name"
                  maxLength={60}
                  value={values.name}
                  onChange={(event) => setValues({ ...values, name: event.target.value })}
                />
              </div>
              {/* MD-01 field 4's "default terms" — the MD-09 term a NEW
                  partner in this group is given when nobody picks one. */}
              <div className="grid gap-1.5">
                <Label htmlFor="pg-term">{t('pgroups.fDefaultTerm')}</Label>
                <NativeSelect
                  id="pg-term"
                  value={values.defaultPaymentTermId}
                  onChange={(event) =>
                    setValues({ ...values, defaultPaymentTermId: event.target.value })
                  }
                >
                  <option value="">{t('pgroups.noDefaultTerm')}</option>
                  {(terms ?? [])
                    .filter((term) => term.isActive || term.id === values.defaultPaymentTermId)
                    .map((term) => (
                      <option key={term.id} value={term.id}>
                        {term.name}
                      </option>
                    ))}
                </NativeSelect>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Gated
                permission={editing ? 'crm.edit' : 'crm.create'}
                reasonKey={editing ? 'perm.cannot.editPartnerGroups' : 'perm.cannot.createPartnerGroups'}
              >
                <Button onClick={() => void submit()} disabled={!values.name.trim()}>
                  {t('common.save')}
                </Button>
              </Gated>
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                {t('common.cancel')}
              </Button>
              {editing ? (
                <Gated permission="crm.edit" reasonKey="perm.cannot.editPartnerGroups">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setActive
                        .mutateAsync({ groupId: editing.id, active: !editing.isActive })
                        .then(() => setFormOpen(false))
                        .catch(fail);
                    }}
                  >
                    {t(editing.isActive ? 'items.switchOff' : 'items.switchOn')}
                  </Button>
                </Gated>
              ) : null}
              {/* MD-RULE-4: Delete hidden while partners are in the group. */}
              {editing && editing.partnersIn === 0 ? (
                <>
                  <Gated permission="crm.delete" reasonKey="perm.cannot.deletePartnerGroups">
                    <Button variant="destructive" onClick={() => setDeleting(true)}>
                      {t('common.delete')}
                    </Button>
                  </Gated>
                  {/* GEN-58 through the shared box (GEN-204/GEN-209), never
                      `window.confirm` — browser chrome is neither of
                      BR-PERM-15's two patterns. */}
                  <DeleteConfirmBox
                    open={deleting}
                    detail={t('pgroups.confirmDelete', { name: editing.name })}
                    onCancel={() => setDeleting(false)}
                    onConfirm={() => void removeGroup(editing)}
                    busy={remove.isPending}
                  />
                </>
              ) : null}
            </div>
            {editing ? <AuthorshipLine record={editing} /> : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid gap-4">
          <Label className="flex items-center gap-2 text-sm font-normal">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            {t('common.showInactive')}
          </Label>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : (groups ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('pgroups.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pgroups.colName')}</TableHead>
                  <TableHead>{t('pgroups.colDefaultTerm')}</TableHead>
                  <TableHead className="text-end">{t('pgroups.colPartners')}</TableHead>
                  <TableHead>{t('pgroups.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(groups ?? []).map((group) => (
                  <TableRow
                    key={group.id}
                    className="cursor-pointer"
                    onClick={() => openEdit(group)}
                  >
                    <TableCell className="font-medium">{group.name}</TableCell>
                    <TableCell>{group.defaultPaymentTermName ?? '—'}</TableCell>
                    <TableCell className="text-end font-mono">
                      {group.partnersIn > 0 ? (
                        // MD-01 field 4's "reporting": the count opens the
                        // partner list sliced to this group.
                        <Link
                          to={`/partners?group=${group.id}`}
                          className="underline underline-offset-2"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {group.partnersIn}
                        </Link>
                      ) : (
                        group.partnersIn
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {t(group.isActive ? 'common.active' : 'common.inactive')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
